<template>
  <div class="notification-dropdown">
    <v-menu
      v-model:menuOpen="menuOpen"
      offset-y
      @click:outside="closeMenu"
      @keydown.esc="closeMenu"
      :close-on-content-click="false"
      right
      :z-index="zIndex"
    >
      <template v-slot:activator="{ props }">
        <v-btn v-bind="props" icon @click="toggleMenu">
          <v-icon>mdi-bell</v-icon>
          <v-badge color="red" v-if="notSeenNotfCount > 0" overlap>
            <template v-slot:badge>
              <span>{{ notSeenNotfCount }}</span>
            </template>
          </v-badge>
        </v-btn>
      </template>
      <v-list class="custom-menu-style">
        <v-list-item>
          <v-list-item-title class="menu-header">
            <v-row no-gutters class="align-center">
              <v-col cols="7"> Notifications </v-col>
              <v-col cols="5">
                <v-switch label="Show Viewed" class="menu-header-switch" v-model="includeSeenNotifications" color="success" hide-details />
              </v-col>
            </v-row>
          </v-list-item-title>
        </v-list-item>
        <v-divider></v-divider>
        <v-list-item v-if="loading">
          <v-list-item-title class="menu-header text-center">
            <v-progress-linear indeterminate color="primary" height="6"></v-progress-linear>
          </v-list-item-title>
        </v-list-item>
        <div v-else-if="notifications.length" class="notifications">
          <v-list-item
            v-for="(item, index) in notifications"
            :key="`${index}_ntfitem`"
            class="notification-item"
            :class="{ dark: isDarkTheme, 'not-seen': !isSeen(item) }"
            @click="makeSeen(item)"
          >
            <notification-detail-content
              :key="`${index}_ntfContent_${notifsVersion}`"
              :item="item"
              :isTeamMember="isTeamMember"
              :isAdmin="isAdmin"
              :isClient="isClient"
              :userId="userId"
              :hasAutoUpdate="true"
              @successRaise="successRaise"
              @errorRaise="errorRaise"
              @initNotifications="initNotifications"
              @initNotSeenNotificationsCount="initNotSeenNotificationsCount"
              @updateItemsVersion="updateItemsVersion"
            />
          </v-list-item>
        </div>
        <v-list-item v-if="viewAllRoute" :to="viewAllRoute" class="show-more" title="View All Notifications" />
      </v-list>
    </v-menu>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import NotificationItem from "../../models/NotificationItem";
import NotificationDetailContent from "./NotificationDetailContent.vue";
import NotificationService from "../../services/NotificationService";
export default defineComponent({
  components: {
    NotificationDetailContent,
  },
  props: {
    userId: {
      type: String,
    },
    isClient: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    isTeamMember: {
      type: Boolean,
      default: false,
    },
    updateNotificationListFlag: {
      type: Boolean,
      default: false,
    },
    viewAllRoute: {
      type: String,
      default: "",
    },
    zIndex: {
      type: String,
      default: "2000",
    },
  },
  data() {
    return {
      notifications: [] as NotificationItem[],
      totalCount: 0,
      pageNumber: 0,
      pageSize: 20,
      menuOpen: false,
      includeSeenNotifications: false,
      replyModel: {
        notificationItemId: "",
        text: "",
        sentByCreator: false,
      },
      notifsVersion: 0,
      notSeenNotfCount: 0,
      loading: true,
      currentNotificationIdToShow: "",
    };
  },
  mounted() {
    this.initNotSeenNotificationsCount();
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    async handleItemClick(item: any) {
      if (item) {
        await this.makeSeen(item);
      }
      this.$nextTick(() => {
        this.menuOpen = true; // Ensure menu remains open
      });
    },
    errorRaise(msg: any) {
      this.$emit("errorRaise", msg);
    },
    successRaise(msg: any) {
      this.$emit("successRaise", msg);
    },
    closeMenu() {
      this.menuOpen = false;
    },
    async initNotSeenNotificationsCount(currentNotificationIdToShow = "") {
      if (this.menuOpen) {
        if (currentNotificationIdToShow) {
          this.currentNotificationIdToShow = currentNotificationIdToShow;
        }
        const replyOpenedItemIds = this.notifications ? this.notifications.filter((x) => (x as any).showReplyBox).map((x) => x.id) : [];
        await this.initNotifications(false);

        // keep open replies
        if (this.notifications) {
          for (let index = 0; index < this.notifications.length; index++) {
            const item = this.notifications[index];
            if (replyOpenedItemIds.includes(item.id)) {
              (item as any).showReplyItems = true;
              (item as any).showReplyBox = true;
              await NotificationService.makeSeenReply(item.id, this.isNotificationCreator(item), this.isClient);
            }
          }
        }
      }

      this.notSeenNotfCount = await NotificationService.getNotSeenNotificationItemsCount(this.isClient);
    },
    async initNotifications(showLoading = true) {
      if (showLoading) {
        this.loading = true;
        this.notifications = [];
      }

      this.pageNumber = 0;
      const result = await NotificationService.getNotificationItems(
        this.pageNumber,
        this.pageSize,
        this.isClient,
        !this.includeSeenNotifications,
        "",
        "",
        this.currentNotificationIdToShow
      );
      this.notifications = result.data;
      this.totalCount = result.totalCount;
      this.loading = false;
    },
    isSeen(item: any) {
      return item.Status === "Seen" || this.isNotificationCreator(item);
    },
    isNotificationCreator(item: any) {
      return item.CreatorUserId === this.userId;
    },
    updateItemsVersion() {
      this.notifsVersion++;
      this.$nextTick(() => {
        this.menuOpen = true; // Ensure menu remains open
        this.initNotSeenNotificationsCount();
      });
    },
    async makeSeen($model: any) {
      if ($model.Status !== "Seen" && !this.isNotificationCreator($model)) {
        $model.Status = "Seen";
        $model.SeenDate = new Date();
        this.currentNotificationIdToShow = $model.id;
        await NotificationService.update($model.id, $model.Status, $model.SeenDate, this.isClient);
        this.initNotSeenNotificationsCount();
      }
    },
  },
  computed: {
    hasMoreItems(): boolean {
      if (this.notifications && this.notifications.length) {
        return this.totalCount > this.notifications.length;
      }

      return false;
    },
    isDarkTheme(): boolean {
      return this.$vuetify.theme.current.dark;
    },
  },
  watch: {
    menuOpen(newVal) {
      if (newVal) {
        this.initNotifications();
      }
    },
    includeSeenNotifications(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.initNotifications();
      }
    },
    updateNotificationListFlag(newVal) {
      this.initNotSeenNotificationsCount();
    },
  },
});
</script>
<style scoped lang="scss">
@import "node_modules/shared-components/assets/colors.scss";

.notification-item {
  border-radius: 4px;
  margin-top: 2px;
}
.notification-item.dark {
  background-color: $dark_gray;
}
.notification-dropdown {
  padding: 0 5px;
  width: 42px;
}
.menu-header {
  font-weight: bold;
}

.custom-menu-style {
  margin-top: 10px;
  border-radius: 0px;
  width: 440px;
}
.notifications {
  overflow-y: auto;
  max-height: calc(100vh - 240px) !important;
}
.notification-item.not-seen {
  border-left: 6px solid $racecar;
  cursor: pointer;
}
.show-more {
  text-align: center;
  font-weight: bold;
  cursor: pointer;
}
</style>
